import { type FC, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { FormGroupUI } from "../../../components/FormGroup";
import { FormGroupLabelUI } from "../../../components/FormGroupLabel";
import { InputUI } from "../../../components/rhfs/Input";
import { RadioUI } from "../../../components/rhfs/Radio";
import { ReactSelectUI } from "../../../components/rhfs/ReactSelect";
import { TextareaUI } from "../../../components/rhfs/Textarea";
import { EMAIL_SENDERS } from "../../../constants/email-senders";
import { DeliveryTiming } from "../../../hooks/useEmailTemplatesRestApi";
import type { OptionItem } from "../../../types/common";

type DistributionGroupOption = {
  id: string;
  name: string;
  contactCount: number;
};

export type EmailTemplateFormValues = {
  id: string;
  name: string;
  from: string;
  distributionGroupId: string;
  subject: string;
  body: string;
  includeSender: boolean;
  deliveryTiming: DeliveryTiming;
};

type EmailTemplateFormFieldsProps = {
  distributionGroups: DistributionGroupOption[];
};

const DELIVERY_TIMING_OPTIONS: OptionItem[] = [
  { label: "今すぐ配信", value: DeliveryTiming.Now },
  { label: "予約配信", value: DeliveryTiming.Scheduled },
];

export const EmailTemplateFormFields: FC<EmailTemplateFormFieldsProps> = ({
  distributionGroups,
}) => {
  const { register } = useFormContext<EmailTemplateFormValues>();
  const groupOptions = useMemo(
    () =>
      distributionGroups.map(group => ({
        label: `${group.name} (${group.contactCount}件)`,
        value: group.id,
      })),
    [distributionGroups],
  );

  const fromOptions = useMemo(
    () =>
      EMAIL_SENDERS.map(sender => ({
        label: `${sender.name} (${sender.email})`,
        value: sender.email,
      })),
    [],
  );

  return (
    <>
      <FormGroupUI className="!space-x-0">
        <FormGroupLabelUI className="w-auto mr-0">
          メールテンプレート名 <span className="text-error">*</span>
        </FormGroupLabelUI>
        <InputUI
          registerName="name"
          className="w-full"
          placeholder="メールテンプレート名を入力"
        />
      </FormGroupUI>

      <FormGroupUI className="!space-x-0">
        <FormGroupLabelUI className="w-auto mr-0">
          配信先グループ <span className="text-error">*</span>
        </FormGroupLabelUI>
        <ReactSelectUI
          registerName="distributionGroupId"
          className="w-full"
          options={groupOptions}
          placeholder="配信先グループを選択..."
          isSetDefault={false}
        />
      </FormGroupUI>

      <FormGroupUI className="!space-x-0">
        <FormGroupLabelUI className="w-auto mr-0">
          送信者 <span className="text-error">*</span>
        </FormGroupLabelUI>
        <ReactSelectUI
          registerName="from"
          className="w-full"
          options={fromOptions}
          placeholder="送信者を選択..."
          isSetDefault={false}
        />
      </FormGroupUI>

      <FormGroupUI className="!space-x-0">
        <div className="form-control w-full max-w-xl">
          <label className="label cursor-pointer justify-start gap-4">
            <input
              type="checkbox"
              className="checkbox checkbox-primary"
              {...register("includeSender")}
            />
            <span className="label-text">自分にも送信する</span>
          </label>
        </div>
      </FormGroupUI>

      <FormGroupUI className="!space-x-0">
        <FormGroupLabelUI className="w-auto mr-0">
          件名 <span className="text-error">*</span>
        </FormGroupLabelUI>
        <InputUI
          registerName="subject"
          className="w-full"
          placeholder="件名を入力"
        />
      </FormGroupUI>

      <FormGroupUI className="!space-x-0">
        <FormGroupLabelUI className="w-auto mr-0">
          本文 <span className="text-error">*</span>
        </FormGroupLabelUI>
        <TextareaUI
          registerName="body"
          className="min-h-[160px]"
          placeholder="本文を入力"
        />
        <div className="text-xs mt-1 text-base-content/60">
          変数: {"{{COMPANY}}"} = 会社名, {"{{NAME}}"} = 担当者名
        </div>
      </FormGroupUI>

      <FormGroupUI className="!space-x-0">
        <FormGroupLabelUI className="w-auto mr-0">
          配信タイミング
        </FormGroupLabelUI>
        <RadioUI
          registerName="deliveryTiming"
          className="space-x-4"
          options={DELIVERY_TIMING_OPTIONS}
        />
      </FormGroupUI>
    </>
  );
};
